import { AudienceTypeEnum } from '../enums/audience-type.enum';

export const AUTOMATIC_AUDIENCES_CATEGORY_NAME_KEY = 'automatic_audiences_translation_key';
export const AUDIENCES_CATEGORY_NAME_KEY = 'audiences_translation_key';
export const COURSE_AUDIENCES_CATEGORY_NAME_KEY = 'course_audiences_translation_key';
export const DEFAULT_AUDIENCE_NAME_KEY = 'default_audience_translation_key';
export const GROW_SIDEBAR_AUDIENCES_CATEGORY_ID = 'grow-sidebar-audiences-category';
export const AUDIENCE_BREADCRUMB_SEPARATOR = ' / ';

export function isAudienceTranslationKey(name: string | null | undefined): boolean {
    return !!name && name.endsWith('_translation_key');
}

export function resolveAudienceDisplayName(name: string | null | undefined, translate: (key: string) => string): string {
    if (!name) {
        return '';
    }

    if (isAudienceTranslationKey(name)) {
        return translate(name);
    }

    return name;
}

export function resolveAudienceBreadcrumbs(breadcrumbs: string | null | undefined, translate: (key: string) => string): string {
    if (!breadcrumbs) {
        return '';
    }

    return breadcrumbs
        .split(AUDIENCE_BREADCRUMB_SEPARATOR)
        .map((segment) => resolveAudienceDisplayName(segment.trim(), translate))
        .filter(Boolean)
        .join(AUDIENCE_BREADCRUMB_SEPARATOR);
}

export function resolveAudienceFullPath(
    audience: { breadcrumbs?: string | null; name?: string | null },
    translate: (key: string) => string
): string {
    const breadcrumbPath = resolveAudienceBreadcrumbs(audience.breadcrumbs, translate);
    const audienceName = resolveAudienceDisplayName(audience.name, translate);

    if (breadcrumbPath && audienceName) {
        return `${breadcrumbPath}${AUDIENCE_BREADCRUMB_SEPARATOR}${audienceName}`;
    }

    return breadcrumbPath || audienceName;
}

export function isAutomaticAudiencesCategory(audience: { type?: number; name?: string } | null | undefined): boolean {
    return audience?.type === AudienceTypeEnum.AUTOMATIC && audience?.name === AUTOMATIC_AUDIENCES_CATEGORY_NAME_KEY;
}

export function isAutomaticAudience(audience: { type?: number; name?: string } | null | undefined): boolean {
    return audience?.type === AudienceTypeEnum.AUTOMATIC && !isAutomaticAudiencesCategory(audience);
}

export function isCourseAudiencesCategory(audience: { name?: string; isAutoGenerated?: boolean } | null | undefined): boolean {
    return audience?.name === COURSE_AUDIENCES_CATEGORY_NAME_KEY && !!audience?.isAutoGenerated;
}

export function isAudiencesCategory(audience: { id?: string; name?: string; isVirtualCategory?: boolean } | null | undefined): boolean {
    return (
        audience?.id === GROW_SIDEBAR_AUDIENCES_CATEGORY_ID ||
        (!!audience?.isVirtualCategory && audience?.name === AUDIENCES_CATEGORY_NAME_KEY)
    );
}

export function isGrowSidebarPromotedCategory(
    audience: { id?: string; name?: string; type?: number; isAutoGenerated?: boolean; isVirtualCategory?: boolean } | null | undefined
): boolean {
    return isAutomaticAudiencesCategory(audience) || isCourseAudiencesCategory(audience) || isAudiencesCategory(audience);
}

export function getRemoveAudienceDialogTexts(
    audience: { name?: string; type?: number },
    translate: (key: string, params?: Record<string, string>) => string
): { confirmTitle: string; confirmMessage: string } {
    const audienceName = resolveAudienceDisplayName(audience.name, translate);

    if (isAutomaticAudience(audience)) {
        return {
            confirmTitle: 'GENERAL.BUTTON.DELETE',
            confirmMessage: translate('ENGAGE.AUDIENCE.DIALOGS.REMOVE_AUTOMATIC_AUDIENCE.MESSAGE', {
                audienceName,
            }),
        };
    }

    return {
        confirmTitle: 'ENGAGE.AUDIENCE.DIALOGS.REMOVE_AUDIENCE.TITLE',
        confirmMessage: translate('ENGAGE.AUDIENCE.DIALOGS.REMOVE_AUDIENCE.MESSAGE', {
            audienceName,
        }),
    };
}

export const BROKEN_AUDIENCE_FILTER_ERROR = 'broken_audience_filter';

export function hasBrokenAudienceFilter(audience: { error?: string | null; isValid?: boolean } | null | undefined): boolean {
    return audience?.isValid === false || audience?.error === BROKEN_AUDIENCE_FILTER_ERROR;
}

export function compareGrowAudienceSidebarChildren(a: { name?: string; type?: number }, b: { name?: string; type?: number }): number {
    if (isAutomaticAudiencesCategory(a)) {
        return -1;
    }

    if (isAutomaticAudiencesCategory(b)) {
        return 1;
    }

    return (a.name || '').localeCompare(b.name || '');
}
